// variant-selector.js
/* Guarded because some page loads evaluate this file twice; see the note in
   modal-manager.js. bundle-manager.js constructs VariantSelector, so the class
   is exported onto window rather than left block-scoped. */
if (!window.VariantSelector) {

class VariantSelector {
  constructor(container) {
    try {
      this.container = container;
      const productCard = container.closest('.product-card');
      const productId = container.dataset.productId;
      const dataScript = document.getElementById(`productData-${productId}`);

      if (!dataScript) {
        throw new Error(`Product data not found for ID ${productId}`);
      }

      // Parse the JSON data from the script tag
      const productData = JSON.parse(dataScript.textContent);

      this.variants = productData.variants;
      this.options = productData.options;
      this.moneyFormat = productData.moneyFormat;
      this.selectedOptions = {};

      // Check if we have a previously selected color from the product card
      const selectedColor = productCard.dataset.selectedColor;
      let colorBtn;

      if (selectedColor) {
        // Use the previously selected color
        colorBtn = this.container.querySelector(`.color-variant-btn[data-option-value="${selectedColor}"]`);
      }

      // If no previously selected color or button not found, use default
      if (!colorBtn) {
        colorBtn = this.container.querySelector('.color-variant-btn[data-default="true"]');
      }

      
      if (colorBtn && colorBtn.dataset.available === 'true') {
        const optionValue = colorBtn.dataset.optionValue;
        const colorOption = this.options.find((opt) => opt.name === 'Color' || opt.name === 'Farbe');
        
        if (colorOption) {
          this.selectedOptions[colorOption.name] = optionValue;

          // Get image data from the color button
          const image = colorBtn.dataset.variantImage;
          const hoverImage = colorBtn.dataset.hoverImage;
          const price = parseInt(colorBtn.dataset.variantPrice || '0', 10);
          const comparePrice = parseInt(colorBtn.dataset.comparePrice || '0', 10);
          const variantId = colorBtn.dataset.variantId;

          // Update product card data attributes
          productCard.dataset.variantId = variantId;
          productCard.dataset.productPrice = price;
          productCard.dataset.productImage = image;
          productCard.dataset.hoverImage = hoverImage;
          productCard.dataset.selectedColor = optionValue;
          productCard.dataset.variantTitle = `Color: ${optionValue}`;

          // Trigger initial update
          const variant = this.findPartialMatchingVariant();
          
          if (variant) {
            this.updateProductCard(
              variant.id,
              variant.price,
              variant.compare_at_price,
              image || variant.featured_image || variant.image,
              hoverImage,
              variant.options
            );

            // Initialize variant option buttons with the correct selected state
            this.options.forEach((option, index) => {
              if (option.name !== 'Color' && option.name !== 'Farbe') {
                const optionValue = variant.options[index];
                this.updateOptionButtonState(option.name, optionValue);
              }
            });
          } else {
            // If no variant found, use the data from the color button
            this.updateProductCard(
              variantId,
              price,
              comparePrice,
              image,
              hoverImage,
              [optionValue, ...Object.values(this.selectedOptions).slice(1)]
            );
          }
        }
      }

      this.initializeEventListeners();
    } catch (error) {
    }
  }

  initializeEventListeners() {

    // Color variant buttons - use event delegation instead of individual listeners
    this.container.addEventListener('click', (e) => {
      const colorBtn = e.target.closest('.color-variant-btn');
      if (colorBtn) {
        e.preventDefault();
        e.stopPropagation();
        this.handleColorVariantClick(e);
      }
    });

    // Find the product card that contains this container
    const productCard = this.container.closest('.product-card');

    // Variant selector button - look in the product card
    const selectorBtn = productCard?.querySelector('.variant-selector-btn');
    
    if (selectorBtn) {
      // Remove any existing listeners to prevent duplicates
      const newBtn = selectorBtn.cloneNode(true);
      selectorBtn.parentNode.replaceChild(newBtn, selectorBtn);
      
      newBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleVariantDropdown();
      });
    } else {
    }

    // Variant option buttons - use event delegation
    this.container.addEventListener('click', (e) => {
      const optionBtn = e.target.closest('.variant-option-btn');
      if (optionBtn) {
        e.preventDefault();
        e.stopPropagation();
        this.handleVariantOptionClick(e);
      }
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!productCard?.contains(e.target)) {
        this.closeVariantDropdown();
      }
    });
  }

  handleColorVariantClick(e) {
    const btn = e.target.closest('.color-variant-btn');
    if (!btn) return;

    const productCard = this.container.closest('.product-card');
    
    // Check if variant is available
    if (btn.dataset.available === 'false') return;

    // Prevent multiple triggers
    if (btn.dataset.processing === 'true') return;

    // Set processing flag
    btn.dataset.processing = 'true';

    try {
      const optionValue = btn.dataset.optionValue;
      const variantId = btn.dataset.variantId;

      // Validate required data
      if (!optionValue || !variantId) {
        return;
      }

      // Update selected state
      this.container.querySelectorAll('.color-variant-btn span').forEach((span) => {
        span.dataset.selected = 'false';
      });
      btn.querySelector('span').dataset.selected = 'true';

      // Store current selected options (except color)
      const currentOptions = { ...this.selectedOptions };
      const colorOption = this.options.find(opt => opt.name === 'Color' || opt.name === 'Farbe');
      if (colorOption) {
        delete currentOptions[colorOption.name];
      }

      // Update selected options with new color
      if (this.options && this.options[0]) {
        this.selectedOptions[this.options[0].name] = optionValue;
      }

      // Find matching variant with all selected options
      const variant = this.findMatchingVariant();

      if (variant) {
        // Use the exact matching variant
        this.updateProductCard(
          variant.id,
          variant.price,
          variant.compare_at_price,
          variant.featured_image || variant.image,
          variant.image,
          variant.options
        );
      } else {
        // If no exact match found, try to find a partial match
        const partialMatch = this.findPartialMatchingVariant();

        if (partialMatch) {
          // Use the partial match data
          this.updateProductCard(
            partialMatch.id,
            partialMatch.price,
            partialMatch.compare_at_price,
            partialMatch.featured_image || partialMatch.image,
            partialMatch.image,
            partialMatch.options
          );
        } else {
          // If no match found at all, use the color button data
          const variantData = this.variants.find(v => v.id === variantId);
          if (variantData) {
            this.updateProductCard(
              variantData.id,
              variantData.price,
              variantData.compare_at_price,
              variantData.featured_image || variantData.image,
              variantData.image,
              [optionValue, ...Object.values(currentOptions)]
            );
          }
        }
      }

      // Update variant option buttons to reflect the new selection
      this.options.forEach((option, index) => {
        if (option.name !== 'Color' && option.name !== 'Farbe') {
          const selectedValue = variant?.options[index] || currentOptions[option.name];
          if (selectedValue) {
            this.updateOptionButtonState(option.name, selectedValue);
          }
        }
      });

    } catch (error) {
    } finally {
      // Clear processing flag after a short delay to prevent rapid re-clicks
      setTimeout(() => {
        btn.dataset.processing = 'false';
      }, 100);
    }
  }

  updateOptionButtonState(optionName, selectedValue) {
    const productCard = this.container.closest('.product-card');
    const optionButtons = productCard?.querySelectorAll(`.variant-option-btn[data-option-name="${optionName}"]`);
    
    if (optionButtons) {
      optionButtons.forEach((btn) => {
        if (btn.dataset.optionValue === selectedValue) {
          btn.classList.add('border-black', 'bg-black', 'text-white');
          btn.classList.remove('border-gray-200', 'bg-white', 'text-black');
          btn.setAttribute('data-selected', 'true');
        } else {
          btn.classList.remove('border-black', 'bg-black', 'text-white');
          btn.classList.add('border-gray-200', 'bg-white', 'text-black');
          btn.setAttribute('data-selected', 'false');
        }
      });
    }
  }

  handleVariantOptionClick(e) {
    const btn = e.target.closest('.variant-option-btn');
    if (!btn) return;
    
    const optionName = btn.dataset?.optionName;
    const optionValue = btn.dataset?.optionValue;

    if (!optionName || !optionValue) {
      return;
    }

    // Ensure we have a color selected first
    const colorOption = this.options.find(opt => opt.name === 'Color' || opt.name === 'Farbe');
    if (colorOption && !this.selectedOptions[colorOption.name]) {
      const defaultColorBtn = this.container.querySelector('.color-variant-btn[data-default="true"]');
      if (defaultColorBtn && defaultColorBtn.dataset.available === 'true') {
        const colorValue = defaultColorBtn.dataset.optionValue;
        this.selectedOptions[colorOption.name] = colorValue;
      }
    }

    // Update selected options
    this.selectedOptions[optionName] = optionValue;

    // Update button state
    this.updateOptionButtonState(optionName, optionValue);

    // Find matching variant
    const variant = this.findMatchingVariant();
    const productCard = this.container.closest('.product-card');
    if (!productCard) return;

    if (variant || Object.keys(this.selectedOptions).length > 0) {
      let updateData = {
        id: variant?.id || '',
        price: variant?.price || 0,
        compare_at_price: variant?.compare_at_price || 0,
        image: variant?.featured_image || variant?.image,
        options: variant?.options || Object.values(this.selectedOptions),
      };

      if (!variant && this.variants) {
        const partialMatch = this.findPartialMatchingVariant();
        if (partialMatch) {
          updateData = {
            id: partialMatch.id,
            price: partialMatch.price,
            compare_at_price: partialMatch.compare_at_price,
            image: partialMatch.featured_image || partialMatch.image,
            options: partialMatch.options,
          };
        }
      }

      if (!updateData.image && variant) {
        updateData.image = variant.featured_image || variant.image;
      }

      this.updateProductCard(
        updateData.id,
        updateData.price,
        updateData.compare_at_price,
        updateData.image,
        null,
        updateData.options
      );
    }

    this.closeVariantDropdown();
  }

  // Add a new method to reset the selected state
  resetSelectedOptions() {
    this.selectedOptions = {};
    this.container.querySelectorAll('.variant-option-btn').forEach((btn) => {
      btn.classList.remove('border-black', 'bg-black', 'text-white');
      btn.classList.add('border-gray-200', 'bg-white', 'text-black');
      btn.setAttribute('data-selected', 'false');
    });
  }

  // Neue Methode zum Finden einer teilweise übereinstimmenden Variante
  findPartialMatchingVariant() {
    if (!this.variants || !this.options) return null;

    return this.variants.find((variant) => {
      return Object.entries(this.selectedOptions).every(([optionName, optionValue]) => {
        const optionIndex = this.options.findIndex((opt) => opt.name === optionName);
        return variant.options[optionIndex] === optionValue;
      });
    });
  }

  findMatchingVariant() {
    if (!this.variants || !this.options) return null;

    // Prüfe ob alle erforderlichen Optionen ausgewählt sind
    const allOptionsSelected = this.options.every((option) => this.selectedOptions[option.name] !== undefined);

    if (!allOptionsSelected) return null;

    return this.variants.find((variant) => {
      return this.options.every((option, index) => variant.options[index] === this.selectedOptions[option.name]);
    });
  }

  findBestMatchingVariant(colorValue) {
    if (!this.variants || !this.options) return null;

    // Get all variants with the selected color
    const colorVariants = this.variants.filter(variant => {
      const colorIndex = this.options.findIndex(opt => opt.name === 'Color' || opt.name === 'Farbe');
      return variant.options[colorIndex] === colorValue;
    });

    if (colorVariants.length === 0) return null;

    // Count matching options for each variant
    const variantMatches = colorVariants.map(variant => {
      let matchCount = 0;
      this.options.forEach((option, index) => {
        if (this.selectedOptions[option.name] === variant.options[index]) {
          matchCount++;
        }
      });
      return { variant, matchCount };
    });

    // Sort by number of matches (descending) and return the best match
    variantMatches.sort((a, b) => b.matchCount - a.matchCount);
    return variantMatches[0]?.variant || null;
  }

  updateProductCard(variantId, price, comparePrice, image, hoverImage, variantOptions) {
    const card = this.container.closest('.product-card');
    if (!card) return;

    // Find the variant to get all its data
    const variant = this.variants.find(v => v.id === variantId);
    if (!variant) return;

    // Update data attributes
    card.dataset.variantId = variantId;
    card.dataset.productPrice = price;
    card.dataset.variantImage = image || variant.featured_image || variant.image;
    card.dataset.variantHoverImage = hoverImage || variant.image;
    card.dataset.variantTitle = variantOptions.join(' / ');
    card.dataset.sku = variant.sku || '';
    card.dataset.comparePrice = comparePrice || variant.compare_at_price || '';

    // Remove old image attributes if they exist
    delete card.dataset.productImage;
    delete card.dataset.hoverImage;

    // If we have a color option, update the selected color
    const colorValue = variantOptions[0];
    if (colorValue) {
      card.dataset.selectedColor = colorValue;
    }

    // Format prices
    const formattedPrice = price ? this.formatMoney(price) : '';
    const formattedComparePrice = comparePrice ? this.formatMoney(comparePrice) : '';

    // Update main image
    if (image) {
      const mainImage = card.querySelector('.product-main-image');
      if (mainImage) {
        mainImage.src = image;
        mainImage.setAttribute('src', image);
      }
    }

    // Update hover image
    const hoverImageToUse = hoverImage || variant.image;
    if (hoverImageToUse) {
      const hoverImageElement = card.querySelector('.product-main-image + img');
      if (hoverImageElement) {
        hoverImageElement.src = hoverImageToUse;
        hoverImageElement.setAttribute('src', hoverImageToUse);
      }
    }

    // Update price display
    const priceElement = card.querySelector('.price');
    const comparePriceElement = card.querySelector('.compare-price');

    if (priceElement) {
      priceElement.textContent = formattedPrice;
      
      if (comparePrice) {
        priceElement.classList.add('text-[#E32C2B]');
        priceElement.classList.remove('text-[#121212]');
      } else {
        priceElement.classList.remove('text-[#E32C2B]');
        priceElement.classList.add('text-[#121212]');
      }
    }

    if (comparePriceElement) {
      if (comparePrice) {
        comparePriceElement.textContent = formattedComparePrice;
        comparePriceElement.classList.remove('hidden');
      } else {
        comparePriceElement.classList.add('hidden');
      }
    }

    // Force a reflow to ensure updates are applied
    card.style.display = 'none';
    card.offsetHeight; // Force reflow
    card.style.display = '';

    // Dispatch variant:changed event
    const event = new CustomEvent('variant:changed', {
      detail: {
        variantId: variant.id,
        price: price,
        comparePrice: comparePrice,
        image: image,
        hoverImage: hoverImage || variant.image,
        variantTitle: variantOptions.join(' / '),
        sku: variant.sku,
        selectedColor: variantOptions[0]
      }
    });
    this.container.dispatchEvent(event);
  }

  // Helper method to get default color title
  getDefaultColorTitle() {
    const defaultColorBtn = this.container.querySelector('.color-variant-btn[data-default="true"]');
    if (defaultColorBtn) {
      const colorOption = this.options.find((opt) => opt.name === 'Color' || opt.name === 'Farbe');
      if (colorOption) {
        return `${colorOption.name}: ${defaultColorBtn.dataset.optionValue}`;
      }
    }
    return '';
  }

  formatMoney(cents) {
    if (!this.moneyFormat) return `${(cents / 100).toFixed(2)}`;
    return this.moneyFormat.replace('{{amount_with_comma_separator}}', (cents / 100).toFixed(2).replace('.', ','));
  }

  toggleVariantDropdown() {
    const productCard = this.container.closest('.product-card');
    const dropdown = productCard?.querySelector('.variant-dropdown');
    
    if (!dropdown) {
      return;
    }

    const isHidden = dropdown.classList.contains('hidden');

    // Close all other dropdowns first
    document.querySelectorAll('.variant-dropdown').forEach((d) => {
      if (d !== dropdown) {
        d.classList.add('hidden');
        d.classList.remove('active');
        const otherBtn = document.querySelector(`[aria-controls="${d.id}"]`);
        if (otherBtn) {
          otherBtn.setAttribute('aria-expanded', 'false');
        }
      }
    });

    // Toggle current dropdown
    if (isHidden) {
      dropdown.classList.remove('hidden');
      dropdown.classList.add('active');
      const selectorBtn = productCard.querySelector('.variant-selector-btn');
      if (selectorBtn) {
        selectorBtn.setAttribute('aria-expanded', 'true');
      }

      // Reattach event listeners to variant option buttons
      const optionButtons = dropdown.querySelectorAll('.variant-option-btn');
      
      // Remove all existing event listeners first
      optionButtons.forEach(btn => {
        const newBtn = btn.cloneNode(true);
        btn.parentNode.replaceChild(newBtn, btn);
      });

      // Add event listeners to the new buttons
      dropdown.querySelectorAll('.variant-option-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.handleVariantOptionClick(e);
        });
      });
    } else {
      dropdown.classList.add('hidden');
      dropdown.classList.remove('active');
      const selectorBtn = productCard.querySelector('.variant-selector-btn');
      if (selectorBtn) {
        selectorBtn.setAttribute('aria-expanded', 'false');
      }
    }
  }

  closeVariantDropdown() {
    const productCard = this.container.closest('.product-card');
    const dropdown = productCard?.querySelector('.variant-dropdown');
    if (dropdown) {
      dropdown.classList.add('hidden');
      dropdown.classList.remove('active');
      const selectorBtn = productCard.querySelector('.variant-selector-btn');
      if (selectorBtn) {
        selectorBtn.setAttribute('aria-expanded', 'false');
      }
    }
  }

  updateOptionButtonStyles(optionName, selectedValue) {
    this.container.querySelectorAll(`.variant-option-btn[data-option-name="${optionName}"]`).forEach((btn) => {
      if (btn.dataset.optionValue === selectedValue) {
        btn.classList.add('border-black');
        btn.classList.remove('border-gray-200');
      } else {
        btn.classList.remove('border-black');
        btn.classList.add('border-gray-200');
      }
    });
  }
}

window.VariantSelector = VariantSelector;

// Add this at the end of the file, just before the DOMContentLoaded event
document.addEventListener('DOMContentLoaded', () => {
  // Initialize variant selectors
  document.querySelectorAll('.product-variants').forEach((container) => {
    new VariantSelector(container);
  });

  // Add event listeners for variant changes
  document.querySelectorAll('.product-card').forEach((card) => {
    card.addEventListener('variant:changed', (event) => {
      const { image, hoverImage, price, comparePrice, variantId } = event.detail;

      // Update main product image
      const productImage = card.querySelector('.product-media img');
      if (productImage && image) {
        productImage.src = image;
        if (hoverImage) {
          productImage.dataset.hoverImage = hoverImage;
        }
      }

      // Update price display
      const priceElement = card.querySelector('.product-price');
      if (priceElement) {
        if (comparePrice) {
          priceElement.innerHTML = `
            <span class="sale-price">${price}</span>
            <span class="compare-price">${comparePrice}</span>
          `;
        } else {
          priceElement.innerHTML = `<span>${price}</span>`;
        }
      }

      // Update variant ID in add to cart form
      const variantInput = card.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = variantId;
      }
    });
  });
});

}
